import * as React from 'react';
import styles from './risk-evaluation-timeline.scss';
import { Badge } from 'ui/badge/badge';
import { useEffect, useMemo, type JSX } from 'react';
import { RiskEvaluationTimelineItem } from 'app/area/case/components/loss-potential/risk-evaluation-timeline/types';
import { useQueryParams } from 'app/area/shared/navigation';
import { tableName } from 'app/area/case/case-data-model';
import { escapeUrl } from 'app/components/favourite-layout/favourite/common/utils';
export interface RiskEvaluationTimelineProps {
  id: string;
  items: RiskEvaluationTimelineItem[];
  selectedItem: RiskEvaluationTimelineItem;
  onClick?: (item: RiskEvaluationTimelineItem) => void;
  className?: string;
  showTooltip?: boolean;
  shouldAnimateOnAdd?: boolean;
  hideRiskWeight?: boolean;
  portalTarget?: HTMLElement;
}

export const RiskEvaluationTimeline = (props: RiskEvaluationTimelineProps): JSX.Element => {
  const { id, className, items, onClick, showTooltip, shouldAnimateOnAdd, hideRiskWeight, portalTarget } = props;
  let ref: HTMLLIElement | null = null;
  const addRef = (el: HTMLLIElement | null) => {
    ref = el;
  };
  const MAX_LENGTH_DATE = 20;

  useEffect(() => {
    document.getElementById(id).scrollLeft = ref?.offsetLeft - 100;
  });

  const isActive = (item: RiskEvaluationTimelineItem) => {
    return JSON.stringify(props.selectedItem) === JSON.stringify(item?.entityPath);
  };

  const handleOnClick = (item: RiskEvaluationTimelineItem) => (e: any) => {
    e.preventDefault();
    onClick?.(item);
  };

  const formattedDate = (date: string) => {
    const truncatedText = date?.substring(0, MAX_LENGTH_DATE);
    return date?.length > MAX_LENGTH_DATE ? truncatedText + '...' : date;
  };

  const isLossPotential = !!useQueryParams().query.addLossPotential;
  const isResidual = !!useQueryParams().query.addResidual;

  const animateItem = (item: RiskEvaluationTimelineItem): boolean => {
    if (!shouldAnimateOnAdd) {
      return false;
    }
    return isLossPotential && item?.entityPath[item?.entityPath.length - 1].hasOwnProperty(tableName.lossPotentialEvaluation) ||
      isResidual && item?.entityPath[item?.entityPath?.length - 1].hasOwnProperty(tableName.lossPotentialResidualEvaluation);
  };

  return <>
    <ul id={id} className={`d-flex justify-content-start ${className} ${styles.riskEvaluationTimeline}`}>
      {items?.map((item, index) => {
        const MemoizedItem = () => {
          const text = hideRiskWeight ? '' : item?.text;
          const active = isActive(item);
          const animate = animateItem(item);

          return useMemo(() => (
            <li key={index}
              className={`${styles.timelineWrapper} ${active && animate ? styles.animateItem : ''}`}
              onClick={(handleOnClick(item))} ref={active ? addRef : undefined}>
              <a className={`${styles.timelineContainer} ${!item.url ? styles.unlinkedItem : ''}`} href={escapeUrl(item.url) || undefined}>
                <span className={`${styles.timelineDate} ${active ? styles.activeItem : ''}`}
                  data-testid={`${active ? 'ActiveItem' : ''}`}>
                  {formattedDate(item?.date)}
                </span>
                <span className={styles.badgeContainer}>
                  <Badge
                    backgroundColor={item?.backgroundColor}
                    foregroundColor={item?.foregroundColor}
                    showTooltip={showTooltip}
                    tooltip={item?.tooltip}
                    text={text}
                    filled={item?.filled}
                    riskTrend={item?.riskTrend} 
                    portalTarget={portalTarget}
                    />
                </span>
              </a>
            </li>
          ), [item.riskTrend?.value]);
        };

        return <MemoizedItem key={index} />;
      })}
    </ul>
  </>;
};
